<template>
  <div :class="$attrs.class">
    <div class="tw-flex-1">
      <h4 class="tw-text-3xl tw-font-black">{{orderDate}}</h4>

      
      <template v-if="props.currentlyEditing">
        <p class="tw-text-2xl tw-mb-4 tw-font-black tw-text-red-500">Currently Being Edited</p>
      </template>

      <template v-if="props.order.status == 'Canceled'">
        <p class="tw-text-2xl tw-mb-4 tw-font-black tw-text-red-500">Canceled</p>
      </template>
      <p class="tw-text-2xl tw-mb-4">{{orderHeadline}}<br>
      <template v-if="(order.deliverymode == 'delivery' || order.deliverymode == 'dispatch') && order.delivery_street1 != null" >
        to {{order.delivery_street1}}
      </template></p>

      <p class="tw-mb-4" v-if="order.products">
        {{order.products.length}} 
        <!-- @vue-skip -->
        {{pluralizeProductName('item', order.products.length)}}
      </p>

      <template v-if="order.catering_order">
        <h4 class="tw-text-2xl tw-font-black" v-if="order.catering_order.event_type != null && order.catering_order.event_type != ''">Event: {{order.catering_order.event_type}}</h4>
        <p v-if="order.catering_order.guest_count">
          {{order.catering_order.guest_count}} 
          <!-- @vue-skip -->
          {{pluralizeProductName('guest', order.catering_order.guest_count)}}
        </p>

        <p v-if="order.catering_order.allergens.length > 0">
          {{order.catering_order.allergens.length + order.catering_order.diets.length}} 
          <!-- @vue-skip -->
          {{pluralizeProductName('allergen', order.catering_order.allergens.length + order.catering_order.diets.length)}} or {{pluralizeProductName('diet', order.catering_order.allergens.length + order.catering_order.diets.length)}}
        </p>
      </template>

      <div class="tw-mt-6">
        <TwButton
          v-if="!props.carousel"
          cssClass="tw-text-xl tw-underline hover:tw-text-orange-medium"
          @click="moreDetailsClick"
          :isLoading="false"
          :buttonText="'More Details'" />
        <TwButton
          v-else
          cssClass="tw-external-input-btn tw-w-1/2 tw-ml-0 md:tw-mr-auto md:tw-ml-auto xl:tw-mr-auto xl:tw-ml-0 2xl:tw-mr-auto 2xl:tw-ml-auto tw-bg-white tw-border-2 tw-border-black tw-text-black tw-text-lg 2xl:tw-text-2xl hover:tw-bg-black hover:tw-border-black hover:tw-text-white tw-font-bold md:tw-px-1 tw-px-4 2xl:tw-px-4 tw-py-3 2xl:tw-py-4 tw-transition-all tw-duration-200 tw-rounded-full"
          @click="moreDetailsClick"
          :isLoading="false"
          :buttonText="'More Details'" />
      </div>

      <div v-if="props.order.fired && props.order.upcoming" class="tw-text-orange-medium tw-italic tw-mt-2 tw-text-lg">
        This order is being prepared
      </div>
    </div>
    <div class="tw-mt-8" v-if="!props.carousel">
      <TwButton
        cssClass="tw-external-input-btn tw-w-full tw-ml-0 md:tw-mr-auto md:tw-ml-auto xl:tw-mr-auto xl:tw-ml-0 2xl:tw-mr-auto 2xl:tw-ml-auto tw-bg-orange-medium tw-border-2 tw-border-orange-medium tw-text-white tw-text-lg 2xl:tw-text-2xl hover:tw-bg-black hover:tw-border-black tw-font-bold tw-px-8 2xl:tw-px-10 tw-py-3 2xl:tw-py-4 tw-transition-all tw-duration-200 tw-rounded-full"
        v-if="!order.upcoming"
        @click="reorderClick"
        :isLoading="props.reordering"
        :disabled="props.disabled"
        :buttonText="reorderButtonText" />
      <div class="2xl:tw-w-full tw-w-1/2 tw-inline-block tw-px-2 2xl:tw-mb-4"
        v-if="order.upcoming && props.editable && order.editable">
        <TwButton
          cssClass="tw-external-input-btn tw-w-full tw-ml-0 md:tw-mr-auto md:tw-ml-auto xl:tw-mr-auto xl:tw-ml-0 2xl:tw-mr-auto 2xl:tw-ml-auto tw-bg-orange-medium tw-border-2 tw-border-orange-medium tw-text-white tw-text-lg 2xl:tw-text-2xl hover:tw-bg-black hover:tw-border-black tw-font-bold md:tw-px-1 tw-px-4 2xl:tw-px-4 tw-py-3 2xl:tw-py-4 tw-transition-all tw-duration-200 tw-rounded-full"
          @click="editOrderClick"
          :isLoading="props.reordering"
          :isDisabled="props.disabled"
          :buttonText="'Edit Order'" />
      </div>
      <div class="2xl:tw-w-full tw-w-1/2 tw-inline-block tw-px-2"
        v-if="order.upcoming">
        <TwButton
        cssClass="tw-external-input-btn tw-w-full tw-ml-0 md:tw-mr-auto md:tw-ml-auto xl:tw-mr-auto xl:tw-ml-0 2xl:tw-mr-auto 2xl:tw-ml-auto tw-bg-white tw-border-2 tw-border-black tw-text-black tw-text-lg 2xl:tw-text-2xl hover:tw-bg-black hover:tw-border-black hover:tw-text-white tw-font-bold md:tw-px-1 tw-px-4 2xl:tw-px-4 tw-py-3 2xl:tw-py-4 tw-transition-all tw-duration-200 tw-rounded-full"
        @click="cancelOrderClick"
        :isLoading="props.reordering"
        :disabled="false"
        :buttonText="'Cancel Order'" />
      </div>
    </div>
  </div>
</template>


<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import TwButton from '../../components/TwButton.vue';

  import type OrderDetails from '../../classes/OrderDetails';
  import type Location from '../../classes/Location';

  import { DateTime } from "luxon";
  const props = defineProps<{ order: OrderDetails, reordering: boolean, editable: boolean, disabled: boolean, index: Number, locations: Location[], carousel: boolean, currentlyEditing: boolean}>();
  const emit = defineEmits(['handleRecentOrderModal', 'handleRecentOrderClick', 'handleEditOrderClick', 'handleCancelOrderClick']);

  const orderDate = computed(() => {
    let dt = '';

    if (props.order?.readytime != null) {
      let ready = DateTime.fromFormat(props.order.readytime, 'yyyyMMdd HH:mm');

      dt = ready.toFormat('EEEE, MMM d')
    }

    return dt;
  })

  const orderLocation = computed(() => {
    let location: (Location | undefined) = undefined;

    if (props.locations && props.locations.length > 0) {
      location = props.locations.find((loc: Location) => {
        return loc.id == props.order.vendorid
      });
    }

    return location
  })

  // Not worth trying to do this one liner in the template so just concate it here
  const orderHeadline = computed(() => {
    let title = ''
    switch(props.order.deliverymode) {
      case 'pickup':
        title += 'Pick up from '
        break;
      case 'drivethru':
        title += 'Drivethru at '
        break;
      case 'curbside':
        title += 'Curbside at '
        break;
      case 'dispatch':
        title += 'Delivery from '
        break;
      default:
        title += 'Pick up from '
    }

    if (orderLocation != null && typeof(orderLocation.value) != 'undefined' && typeof((orderLocation.value as Location).name) != 'undefined') {
      title += 'Piada ' + (orderLocation.value as Location).name
    }

    return title
  })

  const reorderButtonText = computed(() => {
    return "Re-Order - $" + (props.order.total).toLocaleString("en-US", {minimumFractionDigits: 2})
  })

  const moreDetailsClick = (() => {
    emit('handleRecentOrderModal', props.order)
  })

  const reorderClick = (() => {
    emit('handleRecentOrderClick', props.order)
  })

  const editOrderClick = (() => {
    emit('handleEditOrderClick', props.order)
  })

  const cancelOrderClick = (() => {
    emit('handleCancelOrderClick', props.order)
  })
</script>